import { useEffect, useState, useRef } from "react";

// Store the deferred prompt globally so it survives component re-mounts
let globalDeferredPrompt = null;

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  globalDeferredPrompt = e;
});

export function getInstallPrompt() {
  return globalDeferredPrompt;
}

export function canShowInstall() {
  if (window.matchMedia("(display-mode: standalone)").matches) return false;
  if (window.navigator.standalone === true) return false;
  return true;
}

export default function InstallAppBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const promptRef = useRef(null);

  useEffect(() => {
    // Already installed as PWA
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return;

    // Check 24h dismiss cooldown
    try {
      const last = localStorage.getItem("rq_install_dismissed");
      if (last && Date.now() - Number(last) < 24 * 60 * 60 * 1000) return;
    } catch {}

    // Check if global prompt was already captured
    if (globalDeferredPrompt) {
      promptRef.current = globalDeferredPrompt;
      setShowBanner(true);
      return;
    }

    // Listen for future prompt
    function handlePrompt(e) {
      e.preventDefault();
      globalDeferredPrompt = e;
      promptRef.current = e;
      setShowBanner(true);
    }
    window.addEventListener("beforeinstallprompt", handlePrompt);

    // For iOS (no beforeinstallprompt support), show after 2s
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    let timer = null;
    if (isIOS) {
      timer = setTimeout(() => setShowBanner(true), 2000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt);
      if (timer) clearTimeout(timer);
    };
  }, []);

  async function handleInstall() {
    const prompt = promptRef.current || globalDeferredPrompt;
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === "accepted") {
          setShowBanner(false);
          globalDeferredPrompt = null;
          promptRef.current = null;
        }
      } catch {
        // Prompt already used or expired — dismiss the banner
        setShowBanner(false);
        globalDeferredPrompt = null;
        promptRef.current = null;
      }
    } else {
      // No prompt available — show manual instructions
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // Banner already shows iOS instructions
        return;
      }
      // Android/desktop — try opening in Chrome's install flow
      // If that doesn't work, show instructions
      setShowBanner(false);
    }
  }

  function handleDismiss() {
    setShowBanner(false);
    setDismissed(true);
    try { localStorage.setItem("rq_install_dismissed", String(Date.now())); } catch {}
  }

  if (!showBanner || dismissed) return null;

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  return (
    <div
      style={{
        position: "fixed",
        bottom: "16px",
        left: "16px",
        right: "16px",
        zIndex: 99998,
        background: "var(--dark)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 138, 0,0.3)",
        padding: "18px 20px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255, 138, 0,0.1)",
        maxWidth: "480px",
        margin: "0 auto",
      }}
    >
      <img
        src="/app-icons/icon-120.png"
        alt="Remote Quote"
        style={{ width: "48px", height: "48px", borderRadius: "12px", flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
          Get the Remote Quote app
        </div>
        {isIOS ? (
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", marginTop: "3px", lineHeight: 1.4 }}>
            Tap <span style={{ color: "#fff", fontWeight: 600 }}>Share</span> ↗ then <span style={{ color: "#fff", fontWeight: 600 }}>Add to Home Screen</span>
          </div>
        ) : (
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", marginTop: "3px", lineHeight: 1.4 }}>
            Install for quick access from your home screen.
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleInstall}
        style={{
          background: "var(--primary)",
          color: "#fff",
          border: "none",
          borderRadius: "12px",
          padding: "10px 18px",
          fontSize: "0.85rem",
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {isIOS ? "How to install" : "Install"}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        style={{
          background: "none",
          border: "none",
          color: "rgba(255,255,255,0.3)",
          fontSize: "1.3rem",
          cursor: "pointer",
          padding: "2px",
          lineHeight: 1,
          flexShrink: 0,
          position: "absolute",
          top: "8px",
          right: "12px",
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
